import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listClientsFull, listMeetingTypesFull } from "@/api/lookups";
import { useQuery } from "@tanstack/react-query";
import type {
  ClientResponse,
  Folder,
  MeetingSummary,
  MeetingTypeResponse,
} from "@/api/types";
import { useFolders } from "@/hooks/useFolders";

export interface UseMeetingColumnsOptions {
  /** Drop the «Клиент» column — used on the client card where it's redundant. */
  hideClient?: boolean;
  /** Drop the «Участники» column — used on the employee card. */
  hideParticipants?: boolean;
}

/**
 * Shared column builder for the meetings table (TM-035ae774).
 *
 * Extracted from `MeetingsRegistry`'s former private `useColumns` so the
 * employee/client cards can render the same meetings table without
 * duplicating the cell logic. `hideClient`/`hideParticipants` suppress the
 * columns that are redundant on a given card (the client card already knows
 * its client; the employee card already knows the participant).
 *
 * The sortable columns (created_at, meeting_no, status plus the join-display
 * columns client, meeting_type, uploader, participants — TM-035ae774 addendum)
 * carry `meta: { sortable: true }`; whether the indicator actually renders
 * depends on the consumer wiring `sort`/`order`/`onSortChange` into
 * `<DataTable>`. Cards omit those props, so the columns render as plain headers
 * there. Each column `id` matches the backend `sort` key 1:1, so the
 * `onSortChange(columnId)` callback needs no remapping. On cards that hide a
 * column (`hideClient`/`hideParticipants`) the column simply isn't rendered, so
 * its (now sortable) header can't be clicked — nothing breaks.
 *
 * Lookups for client/type names and folder badges are fetched internally so
 * a card can drop the hook in with zero extra plumbing.
 */
export function useMeetingColumns(
  opts: UseMeetingColumnsOptions = {},
): ColumnDef<MeetingSummary, unknown>[] {
  const { folders } = useFolders();
  const { hideClient = false, hideParticipants = false } = opts;

  const clients = useQuery({
    queryKey: ["clients-lookup"],
    queryFn: () => listClientsFull({ limit: 200 }),
  });
  const types = useQuery({
    queryKey: ["meeting-types-lookup"],
    queryFn: () => listMeetingTypesFull(),
  });

  return useMeetingColumnsFrom(
    clients.data ?? [],
    types.data ?? [],
    folders,
    hideClient,
    hideParticipants,
  );
}

/**
 * Pure column factory used by both the data-fetching `useMeetingColumns`
 * (cards) and `MeetingsRegistry` (which already owns the lookup queries and
 * passes them in to avoid duplicate fetches).
 */
export function useMeetingColumnsFrom(
  clients: ClientResponse[],
  types: MeetingTypeResponse[],
  folders: Folder[],
  hideClient = false,
  hideParticipants = false,
): ColumnDef<MeetingSummary, unknown>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    const clientById = new Map(clients.map((c) => [c.id, c] as const));
    const typeById = new Map(types.map((m) => [m.id, m] as const));
    const folderById = new Map(folders.map((f) => [f.id, f] as const));
    const cols: ColumnDef<MeetingSummary, unknown>[] = [
      {
        id: "meeting_no",
        header: t("meetings.columns.meetingNo"),
        meta: { sortable: true },
        // Render as a fixed-width monotonic counter so operators can scan
        // the column at a glance. Falls back to «—» for legacy rows that
        // somehow lack the field (defensive, should never trigger post-T1).
        cell: (info) => info.row.original.meeting_no ?? t("common.dash"),
      },
      {
        id: "created_at",
        header: t("meetings.columns.createdAt"),
        accessorKey: "created_at",
        meta: { sortable: true },
        cell: (info) =>
          new Date(info.getValue() as string).toLocaleString("ru"),
      },
      {
        id: "filename",
        header: t("meetings.columns.filename"),
        cell: (info) => (
          // The whole row is clickable via the parent `<tr onClick>` in
          // `DataTable`. We keep the explicit `<Link>` for semantic anchors
          // (right-click → «Открыть в новой вкладке», keyboard focus) but stop
          // click propagation so the row handler doesn't fire a second
          // duplicate navigation when the user clicks the filename text.
          <Link
            to={"/meetings/$meetingId"}
            params={{ meetingId: info.row.original.id }}
            className="text-primary underline-offset-4 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {info.row.original.original_filename}
          </Link>
        ),
      },
    ];
    if (!hideClient) {
      cols.push({
        id: "client",
        header: t("meetings.columns.client"),
        meta: { sortable: true },
        cell: (info) =>
          clientById.get(info.row.original.client_id)?.name ?? t("common.dash"),
      });
    }
    cols.push(
      {
        id: "meeting_type",
        header: t("meetings.columns.meetingType"),
        meta: { sortable: true },
        cell: (info) =>
          typeById.get(info.row.original.meeting_type_id)?.name ??
          t("common.dash"),
      },
      {
        id: "uploader",
        header: t("meetings.columns.uploader"),
        meta: { sortable: true },
        cell: (info) => info.row.original.uploader_display ?? t("common.dash"),
      },
    );
    if (!hideParticipants) {
      cols.push({
        id: "participants",
        header: t("meetings.columns.participants"),
        meta: { sortable: true },
        cell: (info) => {
          const names = info.row.original.participants_display ?? [];
          return names.length > 0 ? names.join(", ") : t("common.dash");
        },
      });
    }
    cols.push(
      {
        id: "status",
        header: t("meetings.columns.status"),
        meta: { sortable: true },
        cell: (info) => t(`meetings.card.status.${info.row.original.status}`),
      },
      {
        id: "folder",
        header: t("meetings.columns.folder"),
        cell: (info) => {
          const meeting = info.row.original;
          const folderId = meeting.folder_id;
          if (folderId === undefined || folderId === null) {
            return t("common.dash");
          }
          const folder = folderById.get(folderId);
          // Even if the folder lookup hasn't loaded yet we render the badge so
          // an operator scanning the row knows the meeting belongs to *some*
          // folder; the name fills in once `useFolders` resolves.
          return (
            <span
              data-testid={`folder-badge-${meeting.id}`}
              className="inline-flex items-center rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-xs font-medium text-sky-700 dark:text-sky-300"
              aria-label={t("folders.badge.labelAria")}
            >
              {folder?.name ?? folderId}
            </span>
          );
        },
      },
    );
    return cols;
  }, [t, clients, types, folders, hideClient, hideParticipants]);
}
